import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DetailsSubtaskActions } from './DetailsSubtaskActions'
import { FavoriteToggle } from '../FavoriteToggle'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronLeft, ChevronUp } from 'lucide-react'
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion'
import { Button } from '@/src/renderer/components/ui/Button'
import { revealSequence } from '@/src/renderer/lib/motion/revealSequence'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import { whenStill } from '@/src/renderer/lib/motion/whenStill'
import { CalmIllustration } from '../CalmIllustration'
import { ButtonGroup } from '@/src/renderer/components/ui/ButtonGroup'
import { SegmentedControl } from '@/src/renderer/components/ui/SegmentedControl'
import { useDebounce } from '@/src/renderer/hooks/useDebounce'
import type { Task } from '@/src/renderer/lib/db/models'
import { MAX_SUBTASK_DEPTH } from '@/src/renderer/lib/db/taskTree'
import { CanvasWindowCloseIcon } from '../ProjectCanvas/icons'
import { useSubtaskEditor } from '../../hooks/useSubtaskEditor'
import { DetailsRail, groupRailSections } from './DetailsRail'
import { DetailsSection } from './DetailsSection'
import { DetailsHeader } from './DetailsHeader'
import { useSectionSpy } from './useSectionSpy'
import { useDetailsShape } from './useDetailsShape'
import { detailsFingerprints, detailsValues } from './detailsValues'
import {
  isAtEdge, isAtTextEdge, isField, sideStepField, sideStepForKey, stepField, stepForKey,
} from '../../utils/fieldChain'
import { useDetailsSections, type DetailsSectionId, type DetailsChipId } from './useDetailsSections'
import type { DetailsTab, TaskDetailsProps, TaskDetailsViewProps } from './TaskDetails.types'
import { ProjectComments, TaskComments } from '../TaskComments'
import { ProjectDetails } from '../ProjectDetails'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { readLastDetailsTab, rememberDetailsTab } from './lastDetailsTab'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import './TaskDetails.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { DormantIcon } from '@/src/renderer/components/icons'

/** Die Schreibpause, nach der Titel und Notizen in die Datenbank gehen. */
const TYPING_PAUSE = 500

/**
 * **Die Aufgaben-Details** — alles, was an der ausgewählten **Aufgabe** hängt, offen
 * untereinander: Titel, Notizen, Unteraufgaben und die sieben Eigenschaften, dazu links
 * die **Zeichen-Leiste** zum Springen.
 *
 * Sie hängen an der AUSWAHL, nicht an einer Aufgabe: Wer mit den Pfeiltasten durch die
 * Liste läuft, sieht die Eigenschaften mitlaufen. Bei mehreren gilt die ganze Auswahl, und
 * Titel, Notizen und Unteraufgaben fallen weg — drei Titel passen nicht in ein Feld.
 *
 * Die Tastatur gehört der **Liste**: Nichts hier nimmt beim Aufbau den Fokus, und Escape
 * gibt ihn über `onReleaseFocus` zurück, ohne zu schließen.
 *
 * **Eine einzige Gestalt** (ADR 0023): eine schwebende Karte auf der halben
 * Fläche, links daneben die Leiste als Ausbuchtung ihrer Silhouette. Kein
 * Slide-over mehr, und damit auch nichts, was im Betrieb einzufrieren wäre.
 *
 * @example
 * <TaskDetails
 *   tasks={selectedTasks}
 *   onUpdate={handleContextMenuBadgeUpdate}
 *   onClose={() => setDetailsOpen(false)}
 *   onStepTask={dir => navigation.stepToAdjacentTask(dir)}
 *   canStepPrev={canPrevTask}
 *   canStepNext={canNextTask}
 *   onReleaseFocus={() => navigation.wrapperRef.current?.focus()}
 * />
 */
/** Schon ausgeführte Anfragen der Glocke — über das Aushängen hinweg (siehe dort). */
const handledReminderRequests = new WeakSet<object>()

export const TaskDetails: React.FC<TaskDetailsProps> = props => {
  const source = useDataSource()
  /*
    **Eine Unteraufgabe öffnen** (T14 #179, Grill 3): Der Pfeil an ihrer Zeile schaltet
    die Details auf sie um; „Umzug ›" vor dem Titel führt zurück. Das gilt nur, solange
    ihre Aufgabe die Auswahl ist — wer in der Liste weitergeht, sieht die nächste Aufgabe.

    **Ein Stapel statt eines Schritts** (#181): Aus einer Unteraufgabe geht es eine Ebene tiefer,
    „‹ Eltern-Titel" führt eine Ebene zurück. Der Stapel ist die Kette über der gezeigten Zeile —
    gemerkt wird nur sie, der Weg zurück steht in den Daten.
  */
  const [opened, setOpened] = useState<{ parentId: string; childId: string } | null>(null)
  const root = props.tasks.length === 1 ? props.tasks[0] : undefined
  const childId = opened && root?.id === opened.parentId ? opened.childId : undefined
  const child = source.useTask(childId)
  const chain = source.useTaskAncestors(childId)
  // Wer weitergeht oder die Unteraufgabe löscht, ist fertig mit ihr: Zurück auf ihre Aufgabe
  // gekommen (oder das Löschen rückgängig gemacht) zeigen die Details die Aufgabe, nicht wieder sie.
  // Nur eine geladene, gelöschte Zeile zählt: Direkt nach dem Öffnen steht noch das alte Ergebnis da.
  // Ebenso, wenn sie die Aufgabe verlassen hat (herausgelöst, umgehängt).
  const childGone = child != null && child.id === childId && (child.deletedAt != null || child.parentTaskId == null)
  // Beide Abfragen laufen getrennt: Die Kette gilt erst, wenn sie zur geladenen Zeile passt.
  const chainFresh = chain !== undefined && child?.id === childId && (chain[0]?.id ?? null) === (child?.parentTaskId ?? null)
  const inTree = chainFresh && root !== undefined && chain.some(ancestor => ancestor.id === root.id)
  const leftTree = chainFresh && !inTree
  useEffect(() => {
    if (opened && (root?.id !== opened.parentId || childGone || leftTree)) setOpened(null)
  }, [opened, root?.id, childGone, leftTree])
  // Eine Unteraufgabe schreibt für sich — die Liste daneben kennt sie nicht.
  const updateChild = useCallback<TaskDetailsProps['onUpdate']>((id, updates) => {
    void source.updateTask(id, updates)
  }, [source])
  const { t } = useTranslation()
  // **Sie ruht selbst** (#205): schlafen legen oder wecken, nur sie — ihre Aufgabe bleibt, wie sie ist.
  const restChild = useCallback(() => {
    if (!child) return
    if (child.pausedAt != null) {
      void source.updateTask(child.id, wakeTaskUpdate(child, new Date()))
      return
    }
    void source.updateTask(child.id, { pausedAt: new Date() })
    showSuccessToast({ message: t('taskItem.pausedToast'), description: t('taskItem.pausedToastText', { count: 1 }), icon: <DormantIcon size={18} />, tone: 'rest' })
  }, [child, source, t])
  const openSubtask = useCallback((id: string) => {
    if (root) setOpened({ parentId: root.id, childId: id })
  }, [root])
  // Von außen: der Pfeil in der aufgeklappten Karte der Liste — auch an einer tiefen Zeile.
  const request = props.openSubtask
  useEffect(() => {
    if (request) setOpened({ parentId: request.parentId, childId: request.childId })
  }, [request])

  /*
    **Der Reiter bleibt stehen** (#183, entschieden 2026-09-29): Wer auf „Kommentare" steht und mit
    ↑/↓ weitergeht oder eine Unteraufgabe öffnet, sieht deren Kommentare. Deshalb hier und nicht in
    der Ansicht, die je Auswahl neu aufgebaut wird. **Neu geöffnet zeigen die Details den zuletzt
    benutzten Reiter** (Sven, 29.09.2026) — gemerkt auf dem Gerät (`lastDetailsTab`).
  */
  const [tab, setTabState] = useState<DetailsTab>(readLastDetailsTab)
  const setTab = useCallback((next: DetailsTab) => {
    setTabState(next)
    rememberDetailsTab(next)
  }, [])
  /** Angefangene Kommentare je Aufgabe — wer weitergeht und zurückkommt, findet seinen Satz wieder. */
  const [commentDrafts, setCommentDrafts] = useState<ReadonlyMap<string, string>>(() => new Map())
  const setCommentDraft = useCallback((taskId: string, value: string) => {
    setCommentDrafts(prev => new Map(prev).set(taskId, value))
  }, [])
  const openSubtaskComments = useCallback((id: string) => {
    openSubtask(id)
    setTab('comments')
  }, [openSubtask, setTab])
  // Von außen: die **Marke** an einer Zeile.
  const commentsRequest = props.openComments
  useEffect(() => {
    if (!commentsRequest) return
    if (commentsRequest.parentId) setOpened({ parentId: commentsRequest.parentId, childId: commentsRequest.taskId })
    else setOpened(null)
    setTab('comments')
  }, [commentsRequest, setTab])

  // Von außen: die **Glocke** an einer Zeile — Details-Reiter, dann der Chip „Erinnerung“ (einmal).
  const remindersRequest = props.openReminders
  const [chipRequest, setChipRequest] = useState<{ taskId: string; chip: DetailsChipId; tab?: 'at' } | null>(null)
  useEffect(() => {
    // Eine Anfrage gilt EINEM Klick: Die Pane hält sie, solange die Details offen sind — ausgehängt und
    // wieder eingehängt (Klären, Stapel-Modus) ginge das Popover sonst erneut auf.
    if (!remindersRequest || handledReminderRequests.has(remindersRequest)) return
    handledReminderRequests.add(remindersRequest)
    if (remindersRequest.parentId) setOpened({ parentId: remindersRequest.parentId, childId: remindersRequest.taskId })
    else setOpened(null)
    setTab('details')
    setChipRequest({ taskId: remindersRequest.taskId, chip: 'reminders', ...(remindersRequest.tab ? { tab: remindersRequest.tab } : {}) })
  }, [remindersRequest, setTab])
  const clearChipRequest = useCallback(() => setChipRequest(null), [])
  // Die Glocke an einer Unteraufgabe der Checkliste: sie öffnen, mit offenem Chip „Erinnerung“.
  const openSubtaskReminders = useCallback((id: string, tab?: 'at') => {
    openSubtask(id)
    setTab('details')
    setChipRequest({ taskId: id, chip: 'reminders', ...(tab ? { tab } : {}) })
  }, [openSubtask, setTab])

  const parent = chain?.[0]
  const shown = root && child && parent && !child.deletedAt && inTree ? child : root
  // Der Entwurf gehört dem, was dasteht: der Aufgabe — oder ohne Auswahl dem Projekt (#184).
  const draftKey = shown ? shown.id : props.project ? `project:${props.project.id}` : null
  const tabProps = {
    tab,
    onTabChange: setTab,
    commentDraft: draftKey ? commentDrafts.get(draftKey) ?? '' : '',
    onCommentDraftChange: (value: string) => { if (draftKey) setCommentDraft(draftKey, value) },
    onOpenSubtask: openSubtask,
    onOpenSubtaskComments: openSubtaskComments,
    onOpenSubtaskReminders: openSubtaskReminders,
    chipRequest,
    onChipRequestDone: clearChipRequest,
  }
  if (root && child && parent && !child.deletedAt && inTree) {
    return (
      <TaskDetailsView
        key={`child-${child.id}`}
        {...props}
        {...tabProps}
        tasks={[child]}
        focusedSeconds={null}
        onUpdate={updateChild}
        canStepPrev={false}
        canStepNext={false}
        onRest={restChild}
        parent={parent}
        root={root}
        onBackToParent={() => setOpened(parent.id === root.id ? null : { parentId: root.id, childId: parent.id })}
      />
    )
  }
  return <TaskDetailsView key="selection" {...props} {...tabProps} />
}

const TaskDetailsView: React.FC<TaskDetailsViewProps> = ({
  tasks, focusedSeconds, onUpdate, onClose, onReleaseFocus, focusTitleSignal, onRest,
  onStepTask, canStepPrev = false, canStepNext = false, parent, root, onBackToParent, onOpenSubtask,
  onOpenSubtaskComments, onOpenSubtaskReminders, tab = 'details', onTabChange, commentDraft = '', onCommentDraftChange, project,
  onMovedToLifeSpace, chipRequest, onChipRequestDone,
}) => {
  const { t } = useTranslation()
  const anchor = tasks[0]
  const anchorId = anchor?.id
  /*
    **Kein Reiter bei mehreren** (#183) — wie Titel und Notizen: Kommentare gehören EINER Aufgabe.
    Der Reiter bleibt dabei außen stehen; zurück bei einer Aufgabe, gilt er wieder.
  */
  const commentsTab = tasks.length === 1 && anchor !== undefined
  /*
    **Das Projekt trägt denselben Reiter** (#184): ohne Auswahl „Details | Kommentare" am Projekt.
    Der Reiter ist derselbe Zustand — wer auf „Kommentare" steht und mit Escape zum Projekt kommt,
    sieht dessen Kommentare.
  */
  const projectTab = !anchor && project != null
  const shownTab: DetailsTab = commentsTab || projectTab ? tab : 'details'
  const source = useDataSource()
  const taskCommentCount = source.useCommentCount(commentsTab ? anchorId : undefined)
  const projectCommentCount = source.useProjectCommentCount(projectTab ? project.id : undefined)
  const commentCount = projectTab ? projectCommentCount : taskCommentCount
  /*
    **Ohne Kommentare rollt der Reiter mit** (Sven, 06.10.2026): Er steht dann als erste Zeile im
    Rollbereich und geht beim Rollen mit nach oben. Gibt es Kommentare, bleibt er oben stehen — dort
    zeigt er ihre Zahl, und die soll sichtbar bleiben.
  */
  const tabsInline = (commentsTab || projectTab) && commentCount === 0 && shownTab === 'details'
  /*
    **Die Kette über einer Unteraufgabe** (#181) — für zwei Regeln: Auf Ebene 4 trägt sie keine
    Checkliste mehr, und wiederholt sich irgendein Vorfahr, ist ihre Wiederholung gesperrt.
  */
  const chain = useDataSource().useTaskAncestors(tasks.length === 1 && anchor?.parentTaskId != null ? anchorId : undefined)
  const checklist = anchor?.parentTaskId == null || (chain !== undefined && chain.length < MAX_SUBTASK_DEPTH)

  const sections = useDetailsSections(tasks, { checklist })
  /**
   * **Kopf und Rumpf** (entschieden 2026-09-19). Im Kopf steht, was die Aufgabe
   * IST — Notiz, Unteraufgaben, die drei Chips; im Rumpf die vier Bereiche, die
   * offen untereinander rollen. Der Titel steht über beidem, in der Kopfzeile
   * der Karte.
   */
  const headSections = useMemo(() => sections.filter(s => s.place === 'head'), [sections])
  const bodySections = useMemo(() => sections.filter(s => s.place === 'body'), [sections])

  /**
   * **Was der Scroll-Spy misst** — alles, was ein Zeichen trägt, in der
   * Reihenfolge, in der es dasteht (entschieden 2026-09-19): ganz oben der
   * **Titel**, dann die **Unteraufgaben**, dann die Chip-Zeile, dann die vier
   * Bereiche. Die Leiste liest sich damit von oben nach unten wie die Seite.
   *
   * Zwei Sonderfälle:
   * - Der **Titel** hat keinen eigenen Kasten im Rollbereich (er steht oben im
   *   Kartenkopf). Er braucht auch keinen: Solange nichts an der Kante vorbei
   *   ist, nennt der Spy den ERSTEN der Liste — und das ist er.
   * - **Tags und Belohnung** fehlen hier. Sie stehen neben der Aufmerksamkeit
   *   in derselben Zeile, mit derselben Oberkante; der Rollstand kann die drei
   *   nicht trennen, also steht die erste für die Zeile. Die beiden anderen
   *   leuchten, solange ihr Kasten offen steht.
   */
  const spySections = useMemo(
    () => sections.filter(s => s.group !== null && s.id !== 'tags' && s.id !== 'reward'),
    [sections],
  )
  // Was ALLE Ausgewählten tragen. Die Bereiche zeigen nur das — nie den Wert der ersten
  // Aufgabe allein, denn was hier steht, schreibt der nächste Klick auf die ganze Auswahl.
  const values = useMemo(() => detailsValues(tasks), [tasks])
  // Die Unterschrift der Auswahl und der Fingerabdruck je Wert-Bereich — beide stehen im
  // Schlüssel der Bereiche, siehe unten.
  const selectionKey = useMemo(() => tasks.map(task => task.id).join(','), [tasks])
  /*
    **Die Zeile schaltet um, statt zu verschwinden** (Abnahme 21.09.2026,
    F-AD1). Bis dahin fiel sie an einer ruhenden Aufgabe weg — der Rückweg
    fehlte damit genau dort, wo man ihn sucht. Jetzt heißt sie „Aufwecken …",
    sobald ALLE ruhen; solange eine wach ist, bleibt es beim Schlafenlegen
    (dieselbe Misch-Regel wie im **Aufgaben-Menü**, CONTEXT.md).
  */
  const allResting = useMemo(
    () => tasks.length > 0 && tasks.every(task => task.pausedAt != null),
    [tasks],
  )
  const fingerprints = useMemo(() => detailsFingerprints(values), [values])
  // #205, Punkt 5: Unter einer wiederkehrenden Aufgabe gilt ihr Rhythmus — geöffnet über die Aufgabe
  // (`parent`) oder in Demnächst ausgewählt (`parentRecurring`); bei mehreren genügt eine. Seit #181
  // zählt jeder Vorfahr (`chain`), nicht nur der direkte.
  const recurrenceLocked = parent?.recurrence != null || root?.recurrence != null || tasks.some(task => task.parentRecurring)
    || (chain?.some(ancestor => ancestor.recurrence != null) ?? false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const spyId = useSectionSpy(scrollRef, spySections)
  const subtasks = useSubtaskEditor({ taskId: anchorId })

  /**
   * **Welcher Chip im Kopf offen steht** — hier und nicht im Kopf selbst, weil
   * die **Zeichen-Leiste** ihn von außen aufmacht: Ein Klick auf das Tag- oder
   * Belohnungs-Zeichen ist kein Sprung, sondern ein Griff.
   */
  const [openChip, setOpenChip] = useState<DetailsChipId | null>(null)
  /** „Datum & Uhrzeit …“ im Aufgaben-Menü: Der Chip „Erinnerung“ geht gleich beim Anlegen auf diesem Reiter auf. */
  const [reminderTab, setReminderTab] = useState<'at' | null>(null)
  const changeOpenChip = useCallback((id: DetailsChipId | null) => {
    setOpenChip(id)
    // Zu heißt vergessen: Das nächste Öffnen von Hand zeigt wieder die Liste.
    if (id === null) setReminderTab(null)
  }, [])
  /** Wo der Caret im Kopf steckt — die Quelle für „hier bist du" oben. */
  const [headActive, setHeadActive] = useState<DetailsSectionId | null>(null)
  /** Meldet den Titel an die obere Pille, wie es `DetailsHeader` für seine Felder tut. */
  const titleFocusReporter = useMemo(() => ({
    onFocus: () => setHeadActive('title' as DetailsSectionId),
    onBlur: () => setHeadActive(null),
  }), [])

  /**
   * **Die Trennlinie unter dem Kopf steht erst, wenn etwas darunter liegt**
   * (entschieden 2026-09-19). Ganz oben schnitte sie den Titel von seiner
   * eigenen Aufgabe ab; sobald gerollt wird, sagt sie, dass der Kopf über dem
   * Inhalt steht.
   */
  const [scrolled, setScrolled] = useState(false)

  // ── Die Silhouette: Karte plus die Ausbuchtungen der Leiste ──
  // Der Schlüssel sagt, WANN sich die Pillen bewegen — an ihm hängt das
  // Nachlaufen. Nur die Zahl der Ziele je Gruppe zählt; welcher Bereich gerade
  // im Bild steht, ändert an der Form nichts.
  const railKey = useMemo(
    () => groupRailSections(sections).map(g => `${g.group}:${g.sections.length}`).join('|'),
    [sections],
  )
  const rootRef = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  useDetailsShape(svgRef, pathRef, rootRef, railKey)

  // ── Titel und Notizen: Entwurf im Feld, gedrosselt in die Datenbank ──
  // Kein Abschicken-Knopf (CONTEXT.md, Aufgaben-Details): Alle Eigenschaften schreiben
  // sofort, nur der Text hätte sonst eine Sonderregel. Der Preis ist ein Sync-Eintrag
  // je Schreibpause — deshalb gedrosselt.
  const [titleDraft, setTitleDraft] = useState(anchor?.title ?? '')
  const [notesDraft, setNotesDraft] = useState(anchor?.description ?? '')
  const debouncedTitle = useDebounce(titleDraft, TYPING_PAUSE)
  const debouncedNotes = useDebounce(notesDraft, TYPING_PAUSE)

  /**
   * Text gehört nur einer EINZELNEN Auswahl. `onUpdate` gilt für alles, was ausgewählt
   * ist — ein schwebender Titel, der durchginge, während ein zweiter Haken dazukommt,
   * schriebe denselben Satz auf jede Aufgabe. Genau das nimmt das Konzept heraus, indem
   * es Titel, Notizen und Unteraufgaben bei mehreren gar nicht erst zeigt.
   */
  const single = tasks.length === 1

  /**
   * Was zuletzt an wen ging. Ohne das setzte das Nachreichen beim Auswahlwechsel dieselbe
   * Zeile ein zweites Mal ab, wenn die Drosselung Sekundenbruchteile vorher schon
   * zugeschlagen hat und die Liste den neuen Stand noch nicht hereingereicht hat.
   */
  // Sie merken sich AUCH, von welchem gespeicherten Stand aus geschrieben wurde. Ohne das
  // gälte der Merkzettel ewig: Wer denselben Titel später noch einmal tippt, nachdem ihn
  // etwas anderes (Sync, Aufgaben-Menü) inzwischen verändert hat, käme nicht mehr durch.
  const lastTitleWrite = useRef<{ id: string; from: string; value: string } | null>(null)
  const lastNotesWrite = useRef<{ id: string; from: string | null; value: string | null } | null>(null)

  const writeTitle = useCallback((task: Task, draft: string) => {
    const next = draft.trim()
    // Ein leerer Titel wird NICHT geschrieben — dieselbe Regel wie in der aufgeklappten
    // Karte (`useTaskEntryForm.handleSubmit`). Eine namenlose Aufgabe stünde als leere
    // Zeile in der Liste; wer alles wegräumt, sieht ein leeres Feld und tippt weiter.
    if (!next) return
    if (next === task.title) return
    const last = lastTitleWrite.current
    if (last?.id === task.id && last.value === next && last.from === task.title) return
    lastTitleWrite.current = { id: task.id, from: task.title, value: next }
    onUpdate(task.id, { title: next })
  }, [onUpdate])

  const writeNotes = useCallback((task: Task, draft: string) => {
    // Ein leeres Feld heißt „keine Notizen" — `null`, nicht der leere String, sonst stünde
    // die Aufgabe in der Datenbank anders da als eine nie beschriebene. Getrimmt wie in der
    // Karte (`buildCreationData`), damit beide Editoren dasselbe ablegen.
    const next = draft.trim() || null
    const stored = task.description ?? null
    if (next === stored) return
    const last = lastNotesWrite.current
    if (last?.id === task.id && last.value === next && last.from === stored) return
    lastNotesWrite.current = { id: task.id, from: stored, value: next }
    onUpdate(task.id, { description: next })
  }, [onUpdate])

  /**
   * Der Stand des VORIGEN Bildes — Anker, Entwürfe und ob es eine einzelne Auswahl war.
   * Der Wechsel-Effekt darunter liest ihn, bevor der Mitschreib-Effekt am Ende ihn
   * überschreibt; Effekte laufen in Deklarationsreihenfolge, und jener steht weiter unten.
   */
  const previous = useRef({ id: anchorId, task: anchor, title: titleDraft, notes: notesDraft, single })
  /** Ob der Wechsel-Effekt `previous` in diesem Bild schon gesetzt hat. Siehe dort. */
  const justSwitched = useRef(false)

  /**
   * Wechselt die Auswahl, übernimmt der Entwurf den neuen Wert — ohne das stünde der Text
   * der vorigen Aufgabe im Feld der nächsten.
   *
   * Vorher geht der angefangene Satz aber noch an die ALTE Aufgabe: „Wer sofort schreibt,
   * hat dabei nichts zu retten" (CONTEXT.md, Aufgaben-Details). Ohne dieses Nachreichen
   * verschluckte `useDebounce` beim Wechsel bis zu einer halben Sekunde Tipparbeit —
   * sein Aufräumer löscht den noch laufenden Wecker.
   */
  useEffect(() => {
    const before = previous.current
    /*
      Nachgereicht wird NUR, wenn der Satz dabei bei seiner Aufgabe bleibt. `onUpdate`
      weitet auf die ganze Auswahl aus, sobald mehr als eine dasteht und die angesprochene
      darunter ist (`useContextMenuBadgeUpdate.ts:43-46`). Wer mit Shift eine Zeile ÜBER
      der bearbeiteten dazunimmt, wechselt damit den Anker — die neue steht in
      Listenreihenfolge vorn — und bekäme sonst den halb getippten Titel der alten
      aufgedrückt. Dieselbe Gefahr wie beim Erweitern der Auswahl, nur durch eine andere Tür.
    */
    const wouldWiden = tasks.length > 1 && tasks.some(task => task.id === before.id)
    if (before.id !== anchorId && before.task && before.single && !wouldWiden) {
      writeTitle(before.task, before.title)
      writeNotes(before.task, before.notes)
    }
    const seededTitle = anchor?.title ?? ''
    const seededNotes = anchor?.description ?? ''
    setTitleDraft(seededTitle)
    setNotesDraft(seededNotes)
    /*
      **`previous` wird HIER gesetzt, nicht erst im Mitschreib-Effekt.**

      Das `setTitleDraft` oben wirkt erst im nächsten Bild. In DIESEM Bild gehört
      `titleDraft` noch der VORIGEN Aufgabe, während `anchorId` schon der neuen gehört —
      ein Paar, das nicht zusammenpasst. Schriebe der Mitschreib-Effekt dieses Paar
      mit, würde der nächste Wechsel den Satz der vorigen Aufgabe auf die neue schreiben.

      Genau das geschah bei gehaltener Pfeiltaste: React fasste den nächsten Tastendruck
      mit dem Nachziehen des Entwurfs zusammen, und die Titel der durchlaufenen Aufgaben
      überschrieben einander (gemessen im E2E am 12.09.2026 — von sechs Aufgaben blieben
      vier, eine doppelt).
    */
    justSwitched.current = true
    previous.current = { id: anchorId, task: anchor, title: seededTitle, notes: seededNotes, single }
  }, [anchorId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Nur auf den gedrosselten Wert hören: Stünde `anchor` oder `single` in den
  // Abhängigkeiten, liefe der Effekt auch dann, wenn `debouncedTitle` noch der VORIGEN
  // Aufgabe gehört — und schriebe deren Satz auf die neue.
  useEffect(() => {
    if (!anchor || !single) return
    writeTitle(anchor, debouncedTitle)
  }, [debouncedTitle]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!anchor || !single) return
    writeNotes(anchor, debouncedNotes)
  }, [debouncedNotes]) // eslint-disable-line react-hooks/exhaustive-deps

  // Führt den Stand für das nächste Bild mit. Muss NACH dem Wechsel-Effekt stehen.
  const flushOnUnmount = useRef<() => void>(() => {})
  useEffect(() => {
    // Im Bild des Wechsels übersprungen: Dort hat der Wechsel-Effekt `previous` schon
    // richtig gesetzt, und `titleDraft` gehört noch der vorigen Aufgabe (siehe dort).
    if (justSwitched.current) {
      justSwitched.current = false
    } else {
      previous.current = { id: anchorId, task: anchor, title: titleDraft, notes: notesDraft, single }
    }
    // Aus demselben Stand wie das Nachreichen beim Wechsel — ein Paar, nie zwei.
    const snapshot = previous.current
    flushOnUnmount.current = () => {
      if (!snapshot.task || !snapshot.single) return
      writeTitle(snapshot.task, snapshot.title)
      writeNotes(snapshot.task, snapshot.notes)
    }
  })

  /**
   * **Beim Verlassen geht der angefangene Satz noch raus.**
   *
   * `useDebounce` löscht seinen Wecker beim Abbau (`useDebounce.ts:26-28`), und die
   * Aufgaben-Details verschwinden auf vielen Wegen: das Kreuz, der Knopf im Projektkopf,
   * den Canvas öffnen, in den Stapel- oder Listen-Modus wechseln, die letzte Aufgabe
   * löschen, das Projekt verlassen. Ohne das wäre auf JEDEM dieser Wege die letzte halbe
   * Sekunde Tipparbeit wortlos weg — und der Ausgang, den das Konzept als den regulären
   * nennt, wäre der verlustreichste.
   *
   * Über eine Ref und nicht direkt, weil die Aufräumfunktion eines `[]`-Effekts sonst die
   * Entwürfe des ERSTEN Bildes festhielte.
   */
  useEffect(() => () => { flushOnUnmount.current() }, [])

  // ── Caret in den Titel, auf Zuruf ──
  // Der erste Lauf ist bewusst leer: Beim Aufbau nehmen die Aufgaben-Details niemandem
  // den Fokus.
  const titleRef = useRef<HTMLInputElement>(null)
  const lastFocusSignal = useRef(focusTitleSignal)
  useEffect(() => {
    if (focusTitleSignal === undefined) return
    if (lastFocusSignal.current === focusTitleSignal) return
    lastFocusSignal.current = focusTitleSignal
    const field = titleRef.current
    if (!field) return
    field.focus()
    // **Ans Ende, nicht markiert** (entschieden 2026-09-19): Wer den Titel
    // ausdrücklich ansteuert, will in aller Regel anhängen — markiert hieße, das
    // nächste Zeichen löscht den ganzen Namen.
    field.setSelectionRange(field.value.length, field.value.length)
  }, [focusTitleSignal])

  // Am Rollbereich und nicht am Fenster: Nur DIESER Kasten rollt, und ein
  // inaktiver Tab (display: none) meldet nie.
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const onScroll = () => setScrolled(container.scrollTop > 0)
    onScroll()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => container.removeEventListener('scroll', onScroll)
  }, [anchorId])

  // ── Die Zeichen-Leiste: zwei Pillen, zwei Bedeutungen ──
  /**
   * **„Hier bist du" folgt dem Rollstand** — über BEIDE Pillen hinweg
   * (entschieden 2026-09-19): oben der Stift, beim Rollen die Zeichen darunter,
   * bis man bei **Wann** ankommt, dann dieses, und so weiter.
   *
   * Zwei Dinge gehen vor, weil sie ausdrücklicher sind als ein Rollstand:
   * ein offener Chip-Kasten (dort ist man gerade), und der **Fokus** in einem
   * Feld des Kopfes (wer im Titel schreibt, ist im Titel — egal, wie weit der
   * Rumpf darunter gerollt ist).
   */
  const activeId = headActive ?? openChip ?? spyId

  /**
   * **Ein Klick in der oberen Pille ist ein GRIFF, kein Sprung.** Er holt den
   * Caret (Titel, Unteraufgaben) oder öffnet das Popover (Tags,
   * Belohnung) — ein Sprung wäre folgenlos, weil der Kopf ohnehin oben steht.
   * Die untere Pille springt wie bisher.
   */
  const handleJump = useCallback((id: DetailsSectionId) => {
    const container = scrollRef.current
    if (!container) return

    if (id === 'attention' || id === 'tags' || id === 'reward' || id === 'reminders') {
      container.scrollTo({ top: 0, behavior: 'smooth' })
      if (id !== 'reminders') setReminderTab(null)
      setOpenChip(id)
      return
    }
    if (id === 'title' || id === 'subtasks') {
      container.scrollTo({ top: 0, behavior: 'smooth' })
      const field = id === 'title'
        ? titleRef.current
        : container.querySelector<HTMLInputElement>('.subtask-row--open .subtask-row__title')
      if (!field) return
      field.focus()
      // Ans ENDE, nicht markiert: Man will anhängen, nicht ersetzen.
      field.setSelectionRange(field.value.length, field.value.length)
      return
    }

    const target = container.querySelector<HTMLElement>(`[data-section="${id}"]`)
    if (!target) return
    const containerRect = container.getBoundingClientRect()
    // Inaktiver Tab (display: none) misst nur Nullen (CLAUDE.md) — ein Sprung daraus
    // landete bei 0 und risse den Kasten nach oben, sobald er wieder sichtbar wird.
    if (containerRect.height === 0) return
    const delta = target.getBoundingClientRect().top - containerRect.top
    container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' })
  }, [])

  /*
    **Die Zeichen-Leiste bleibt auch auf „Kommentare"** (#183): Sie ist Teil der Außenlinie der
    Karte (ADR 0023), ausblenden ließe die Karte springen. Ein Klick darauf schaltet zurück auf
    „Details" und springt dann — erst im nächsten Bild, wenn der Rollbereich wieder Maße hat.
  */
  const [pendingJump, setPendingJump] = useState<DetailsSectionId | null>(null)
  const jump = useCallback((id: DetailsSectionId) => {
    if (shownTab === 'comments') {
      onTabChange?.('details')
      setPendingJump(id)
      return
    }
    handleJump(id)
  }, [shownTab, onTabChange, handleJump])
  useEffect(() => {
    if (pendingJump === null || shownTab !== 'details') return
    setPendingJump(null)
    handleJump(pendingJump)
  }, [pendingJump, shownTab, handleJump])

  /*
    Ein Chip von außen (die Glocke an der Zeile) — erst, wenn SEINE Aufgabe dasteht, und erst, wenn
    der Chip STILLSTEHT (gemeldet 30.09.2026): Die Karte fährt beim Öffnen herein, und ein Popover,
    das dabei aufgeht, stünde an der alten Stelle und spränge erst später nach.

    Das Warten hängt nicht am Effekt: `onChipRequestDone` leert die Anfrage sofort, und ein
    Aufräumen beim nächsten Lauf bräche das Warten ab. Abgebrochen wird es erst beim Aushängen.
  */
  const jumpRef = useRef(handleJump)
  jumpRef.current = handleJump
  const cancelChipWait = useRef<(() => void) | null>(null)
  useEffect(() => () => cancelChipWait.current?.(), [])
  useEffect(() => {
    if (!chipRequest || chipRequest.taskId !== anchorId || shownTab !== 'details') return
    onChipRequestDone?.()
    setReminderTab(chipRequest.tab ?? null)
    const chip = chipRequest.chip
    cancelChipWait.current?.()
    cancelChipWait.current = whenStill(
      () => rootRef.current?.querySelector(`[data-chip="${chip}"]`) ?? null,
      () => jumpRef.current(chip),
    )
  }, [chipRequest, anchorId, shownTab, onChipRequestDone])

  /**
   * **Enter verlässt die Eingabe** (Nutzer-Vorgabe, 2026-09-19) — dieselbe
   * Geste wie in der aufgeklappten Karte, nur ohne Abschicken: Hier gibt es
   * nichts abzuschicken, es wird gedrosselt geschrieben. Der angefangene Satz
   * geht dabei SOFORT raus, sonst verlöre man beim Verlassen bis zu eine halbe
   * Sekunde Tipparbeit.
   *
   * **Shift+Enter gehört der Notiz**: Es ist der einzige Weg zu einem harten
   * Zeilenumbruch — genau wie in der Karte (`TaskEntry.handleNotesKeyDown`).
   */
  const leaveField = useCallback((e: React.KeyboardEvent, write: () => void) => {
    e.preventDefault()
    write()
    onReleaseFocus?.()
  }, [onReleaseFocus])

  /**
   * **↑ und ↓ gehen ein Feld weiter** (Nutzer-Vorgabe, 2026-09-19) — Titel, Notiz und
   * die Zeilen der Checkliste hängen als eine Kette untereinander
   * (`fieldChain`).
   *
   * Nur an der KANTE des Feldes: In einer mehrzeiligen Notiz gehört der Anschlag erst
   * ihr. Die aufgeklappte Karte macht das ohne diese Rücksicht
   * (`TaskEntry.handleNotesKeyDown`) — dort kommt man mit den Pfeiltasten nicht durch
   * drei Absätze.
   *
   * Am Anfang und am Ende der Kette passiert nichts: Die Kette sind die drei Felder,
   * kein Weg in die Bereiche darunter.
   *
   * **← und → wechseln zwischen Notiz und Unteraufgaben**, solange die beiden
   * nebeneinander stehen — und auch nur dann am Rand des Textes, sonst käme man mit den
   * Pfeiltasten nicht mehr durch ein Wort.
   */
  const handleFieldArrow = useCallback((e: React.KeyboardEvent): boolean => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false
    const field = e.target
    if (!isField(field)) return false

    const step = stepForKey(e.key)
    const side = sideStepForKey(e.key)
    const moved = step !== null
      ? isAtEdge(field, step) && stepField(rootRef.current, field, step)
      : side !== null
        && isAtTextEdge(field, side)
        && sideStepField(rootRef.current, field, side)
    if (!moved) return false

    e.preventDefault()
    e.stopPropagation()
    return true
  }, [])

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (handleFieldArrow(e)) return
    if (e.key !== 'Enter' || !anchor) return
    leaveField(e, () => writeTitle(anchor, titleDraft))
  }, [anchor, titleDraft, writeTitle, leaveField, handleFieldArrow])

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (handleFieldArrow(e)) return
    if (e.key !== 'Enter' || !anchor) return
    // Shift+Enter gehört dem Feld — Zeilenumbruch, kein Verlassen.
    if (e.shiftKey) { e.stopPropagation(); return }
    leaveField(e, () => writeNotes(anchor, notesDraft))
  }, [anchor, notesDraft, writeNotes, leaveField, handleFieldArrow])

  /**
   * Escape gibt die Tastatur an die Liste zurück und schließt NICHT: Der Ausgang ist das
   * Kreuz, und wer nur aus einem Feld heraus will, will nicht die Spalte los.
   *
   * `stopPropagation` hält die Taste im React-Baum auf — sie erreicht keinen Griff
   * OBERHALB der Aufgaben-Details. Griffe, die direkt an `window` oder `document` hängen,
   * hört sie nicht: Die laufen außerhalb des Baums und sehen das Ereignis weiterhin.
   * Wer die abschirmen will, muss dort ansetzen, nicht hier.
   *
   * Verbraucht wird die Taste auch ohne `onReleaseFocus` — sonst verhielte sich derselbe
   * Griff je nach Aufrufer anders.
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    onReleaseFocus?.()
  }, [onReleaseFocus])

  const tabs = (commentsTab || projectTab) && (
    <div
      className={`task-details__tabs${tabsInline ? ' task-details__tabs--inline' : ''}`}
      data-scrolled={!tabsInline && scrolled && shownTab === 'details' ? '' : undefined}
    >
      <div className="task-details__tabs-inner">
        <SegmentedControl
          aria-label={t('taskDetails.tabs.label')}
          value={shownTab}
          onValueChange={value => onTabChange?.(value)}
          options={[
            { value: 'details', label: t('taskDetails.tabs.details') },
            { value: 'comments', label: t('taskDetails.tabs.comments'), ...(commentCount > 0 ? { badge: commentCount } : {}) },
          ]}
        />
      </div>
    </div>
  )

  return (
    <aside
      ref={rootRef}
      /*
        `nokey` ist kein Zierrat und keine BEM-Klasse, sondern React Flows
        Ausstiegsluke: `isInputDOMNode` (`@xyflow/system`) nimmt als Ausnahme
        nur INPUT/SELECT/TEXTAREA, `contenteditable` — und alles, was unter
        einem `.nokey` liegt. Ohne sie spannte die Leertaste auf einem Knopf in
        den Aufgaben-Details das Schieben der Fläche DARUNTER scharf und
        drückte gleichzeitig den Knopf: zwei Bedeutungen für einen Anschlag.
        Dieselbe Art Griff, mit der `CanvasFlow` schon `deleteKeyCode={null}`
        setzt.
      */
      className="task-details nokey"
      aria-label={t('taskDetails.title')}
      onKeyDown={handleKeyDown}
    >
      {/*
        **Karte und Leiste haben EINE Außenlinie.** Grund, Rahmen und die drei
        Ausbuchtungen kommen aus einem Pfad (`detailsShape.ts`), nicht aus
        `border-radius` an zwei Kästen — eine Naht zwischen Pille und Karte
        gäbe es sonst bei jedem Zoomfaktor woanders. Dieselbe Idee wie die
        Reiter-Form des Fensterrahmens, nur um 90° gedreht.
      */}
      <svg ref={svgRef} className="task-details__shape" aria-hidden="true" focusable="false">
        <path ref={pathRef} className="task-details__shape-path" d="" />
      </svg>

      <div className="task-details__panel">
        {/*
          Der Kopf rollt NICHT mit: Titel, Pfeile und das Kreuz bleiben
          erreichbar, auch wenn der Inhalt weit unten steht. Die Trennlinie
          darunter ist die Kante, an der der Rollbereich anfängt.
        */}
        {/*
          **Der Titel steht HIER und nur hier** (entschieden 2026-09-19, nach dem
          Entwurf): oben neben den Pfeilen, als Feld. Er stand eine Weile zweimal
          da — als Beschriftung im Kopf und als Feld darunter im Rumpf; das war
          derselbe Satz an zwei Orten.
        */}
        <header className={`task-details__head${tabsInline ? ' task-details__head--tabs-inline' : ''}`} data-scrolled={scrolled ? '' : undefined}>
          {anchor && tasks.length === 1 ? (
            <div className={`task-details__head-title${parent ? ' task-details__head-title--child' : ''}`} {...titleFocusReporter}>
              {parent && onBackToParent && (
                <button
                  type="button"
                  className="task-details__parent"
                  aria-label={t('taskDetails.backToParent', { title: parent.title })}
                  onClick={onBackToParent}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  <span className="task-details__parent-title">{parent.title}</span>
                </button>
              )}
              <input
                ref={titleRef}
                type="text"
                className="task-details__head-label"
                aria-label={t('taskDetails.section.title')}
                // Wer den Titel leer räumt, stand sonst vor einer leeren Zeile neben den
                // Pfeilen — nichts sagte, dass dort ein Feld ist (Nutzer-Vorgabe,
                // 2026-09-19). Wie bei der Notiz darunter.
                placeholder={t('taskDetails.header.titlePlaceholder')}
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onKeyDown={handleTitleKeyDown}
              />
            </div>
          ) : !anchor && project ? (
            // Das Projekt (#220): Name und Icon nur zum Lesen — bearbeitet wird im Projektkopf.
            <div className="task-details__head-title task-details__head-title--project">
              <ProjectIcon icon={project.icon} iconColor={project.iconColor} size={20} />
              <span className="task-details__head-label task-details__head-label--project">{project.name}</span>
            </div>
          ) : (
            <span className="task-details__head-label">
              {!anchor
                ? t('taskDetails.title')
                : t('taskDetails.head.many', { count: tasks.length })}
            </span>
          )}
          <div className="task-details__head-actions">
            {/*
              **Die Pfeile blättern die Auswahl weiter** und überspringen dabei
              Abschnittsköpfe, wo die Pfeiltasten anhalten (ADR 0022): Die
              Taste bewegt durch die LISTE, wo ein Kopf ein Ort ist, an dem man
              etwas tut. Die Pfeile sitzen im Kopf der Aufgaben-Details, deren
              einzige Aufgabe es ist, eine Aufgabe zu zeigen — ein „Weiter",
              das auf „Keine Aufgabe ausgewählt" landet, ist ein toter Schritt.

              Am Rand sind sie AUS und nicht weg, damit die Gruppe nicht die
              Breite wechselt.

              Dieselbe `ButtonGroup` wie der Liste/Stapel-Umschalter im
              Projektkopf — Form, Hover und Druck kommen von dort, nicht aus
              einem Nachbau.
            */}
            {/* Eine Unteraufgabe: herauslösen oder umhängen (#204). */}
            {parent && anchor && tasks.length === 1 && <DetailsSubtaskActions subtask={anchor} parent={root ?? parent} />}
            {/*
              **Der Stern** (#210) — für die ganze Auswahl über `onUpdate`; wer schon Favorit ist, behält
              seinen Platz. Nicht an Unteraufgaben: Favoriten sind Projekte und Aufgaben.
            */}
            {anchor && !parent && tasks.every(task => task.parentTaskId == null) && (
              <FavoriteToggle
                favorite={tasks.every(task => task.favoriteOrder != null)}
                onFavoriteChange={favorite => onUpdate(anchor.id, { favoriteOrder: favorite ? Date.now() : null })}
                subjectIds={tasks.map(task => task.id)}
                className="task-details__favorite"
              />
            )}
            <ButtonGroup
              iconOnly
              className="task-details__step"
              aria-label={t('taskDetails.stepGroup')}
              items={[
                {
                  value: 'prev',
                  label: t('taskDetails.previousTask'),
                  icon: <ChevronUp aria-hidden="true" />,
                  disabled: !canStepPrev,
                },
                {
                  value: 'next',
                  label: t('taskDetails.nextTask'),
                  icon: <ChevronDown aria-hidden="true" />,
                  disabled: !canStepNext,
                },
              ]}
              onSelect={value => onStepTask?.(value === 'prev' ? 'prev' : 'next')}
            />
            <WithTooltip content={t('taskDetails.close')}>
              <button
                type="button"
                className="task-details__close"
                aria-label={t('taskDetails.close')}
                onClick={onClose}
              >
                <CanvasWindowCloseIcon size={20} />
              </button>
            </WithTooltip>
          </div>
        </header>

        {!tabsInline && tabs}

        {!anchor ? (
          /*
            **Ohne Auswahl bleiben sie stehen und sagen das in einer Zeile.**

            Sie machen sich NICHT selbst zu. Die Auswahl kann unterwegs leer
            werden, ohne dass jemand die Aufgaben-Details weghaben will: Ein
            **Abschnittskopf** ist eine Station der Pfeiltasten und hebt die
            Aufgaben-Auswahl auf. Gingen sie dabei zu, risse genau der Ablauf ab,
            um den es geht — „Wer mit den Pfeiltasten durch die Liste läuft, sieht
            die Eigenschaften mitlaufen" (CONTEXT.md). Die nächste Zeile füllt sie
            wieder.

            Kein Widerspruch zum „leeren Versprechen" im Konzept: Das handelt vom
            ÖFFNEN ohne Auswahl — dafür rastet der Knopf weiterhin gar nicht erst
            ein. **Auf der Projektseite ist ganz ohne Auswahl das Projekt da**
            (#220); die Zeile bleibt für den Abschnittskopf.

            Zugemacht wird nur über das Kreuz und den Knopf; beide stehen weiter
            da. Der Umriss der Leiste hält dabei seine Höhe (siehe
            `useDetailsShape`), damit er bei jedem Abschnittskopf nicht zuckt.
          */
          project ? (
            shownTab === 'comments' ? (
              <div className="task-details__comments">
                <ProjectComments
                  projectId={project.id}
                  draft={commentDraft}
                  onDraftChange={value => onCommentDraftChange?.(value)}
                  onReleaseFocus={onReleaseFocus}
                />
              </div>
            ) : (
              <div className="task-details__scroll">
                {tabsInline && tabs}
                <div className="task-details__content">
                  <ProjectDetails project={project} onMovedToLifeSpace={onMovedToLifeSpace} />
                </div>
              </div>
            )
          ) : (
            <TaskDetailsEmpty onSelectNext={canStepNext ? () => onStepTask?.('next') : undefined} />
          )
        ) : (
          <>
          {/*
            **„Kommentare" füllt die ganze Karte.** Der Rollbereich der Details bleibt dabei stehen,
            nur verborgen: So kommt man auf derselben Rollhöhe zurück, und seine Beobachter (Leiste,
            Trennlinie) hängen weiter. Verborgen misst er Nullen — beide steigen dann aus.
          */}
          {shownTab === 'comments' && (
            <div className="task-details__comments">
              <TaskComments
                task={anchor}
                draft={commentDraft}
                onDraftChange={value => onCommentDraftChange?.(value)}
                onReleaseFocus={onReleaseFocus}
              />
            </div>
          )}
          <div className="task-details__scroll" ref={scrollRef} hidden={shownTab === 'comments'}>
            {tabsInline && tabs}
            {/*
              **Die Höchstbreite steht an EINER Stelle, nicht an zehn.** Auf der
              halben Fläche eines breiten Fensters liefe ein Kalender sonst
              quer durch die Karte; die Überschrift läuft mit, sonst stünde sie
              verrutscht über ihrem Kasten.
            */}
            <div className="task-details__content">
              {/*
                **Der Kopf rollt MIT.** Er ist kein feststehender Balken: Bei vier
                Kästen kostete er sonst dauerhaft Höhe, die die Kalender brauchen.
                Der feste Teil ist der Kartenkopf darüber (Titelzeile, Pfeile, Kreuz).
              */}
              <DetailsHeader
                sections={headSections}
                anchorId={anchor.id}
                values={values}
                onUpdate={onUpdate}
                notesDraft={notesDraft}
                onNotesDraftChange={setNotesDraft}
                onNotesKeyDown={handleNotesKeyDown}
                onSubtasksKeyDown={handleFieldArrow}
                onOpenSubtask={onOpenSubtask}
                onOpenSubtaskComments={onOpenSubtaskComments}
                onOpenSubtaskReminders={onOpenSubtaskReminders}
                subtasks={subtasks}
                openChip={openChip}
                onOpenChipChange={changeOpenChip}
                reminderTab={reminderTab}
                onActiveChange={setHeadActive}
              />
              {bodySections.map(section => (
                /*
                  **Ein Wert-Bereich hängt an seinem WERT, ein Text-Bereich an der Auswahl.**

                  Die Inhalte setzen ihren sichtbaren Zustand einmal beim Aufbau aus `value`
                  und lesen ihn danach nie wieder (siehe `detailsFingerprints`). Ändert sich
                  der Wert von außen, muss der Bereich deshalb neu aufbauen — sonst zeigt er
                  den alten weiter und schreibt ihn beim nächsten **Bestätigen** zurück. Das
                  gilt für jeden Weg von außen: das Kreuz im Kopf des Bereichs, eine **Marke**
                  an der Zeile, das **Aufgaben-Menü**, ein anderer Anker, eine erweiterte
                  Auswahl.

                  Der Wert ist dafür der genauere Schlüssel als die Auswahl. Er deckt alle
                  diese Wege ab — die Auswahl deckte nur den Ankerwechsel — und er lässt den
                  Bereich stehen, wo zwei Aufgaben sich einig sind: Wer mit den Pfeiltasten
                  durch eine Liste ohne Termine läuft, baut die Kalender nicht bei jeder Zeile
                  neu auf.

                  Der Preis ist der flüchtige Zustand eines Bereichs, sobald sein Wert sich
                  ändert: das Suchfeld im Tag-Kasten leert sich beim Anhaken. Das ist der
                  richtige Tausch — ein stehengebliebener Wert wird zurückgeschrieben, ein
                  geleertes Suchfeld ist getippt.
                */
                <DetailsSection
                  key={`${section.id}:${fingerprints[section.id] ?? selectionKey}`}
                  section={section}
                  anchorId={anchor.id}
                  values={values}
                  onUpdate={onUpdate}
                  focusedSeconds={tasks.length === 1 ? focusedSeconds : undefined}
                  onRest={tasks.length > 0 ? onRest : undefined}
                  resting={allResting}
                  recurrenceLocked={recurrenceLocked}
                />
              ))}
            </div>
          </div>
          </>
        )}
      </div>

      {/*
        **Nach dem Panel und nicht davor.** Die erste Pille liegt auf Höhe des
        Kopfes und ragt in die Karte hinein; stünde sie im Baum davor, malte
        der Kopf über ihren getönten Kasten.
      */}
      <div className="task-details__rail">
        <DetailsRail sections={sections} activeId={activeId} onJump={jump} />
      </div>
    </aside>
  )
}

/**
 * Aufgaben-Details ohne Auswahl: dasselbe Muster wie die leeren Seiten —
 * Karopapier (Figma 5462:195059), der Satz, ein Knopf zur nächsten Aufgabe —
 * mit derselben Einblendung (`revealSequence`, gewünscht 25.09.2026).
 *
 * Kein `EmptyState`: dessen Überschrift und Mindesthöhe sind für eine
 * Seitenleiste zu groß. Den Sichtbarkeits-Beobachter von dort braucht es auch
 * nicht — der Zweig wird bei jedem Leerwerden neu eingehängt, die Einblendung
 * läuft also beim Einhängen — sobald es ruhig ist (`whenCalm`).
 *
 * Der Knopf fehlt am Ende der Liste: Ein grauer Knopf ohne Ziel wäre dort nur
 * im Weg.
 */
const TaskDetailsEmpty: React.FC<{ onSelectNext?: () => void }> = ({ onSelectNext }) => {
  const { t } = useTranslation()
  const reduced = useReducedMotion() ?? false
  const reveal = useMemo(() => revealSequence({ reduced }), [reduced])
  const controls = useAnimationControls()
  // Erst anlaufen, wenn es ruhig ist — sonst fällt die Einblendung in die
  // Arbeit, die das Leerwerden auslöst (siehe `whenCalm`).
  useEffect(() => whenCalm(() => void controls.start('shown')), [controls])

  return (
    <motion.div className="task-details__empty" initial="hidden" animate={controls}>
      <motion.div className="task-details__empty-illustration" aria-hidden="true" variants={reveal.illustration}>
        <CalmIllustration set="desk" motif="gridPaper" />
      </motion.div>
      <motion.p className="task-details__empty-text" variants={reveal.text}>
        {t('taskDetails.empty')}
      </motion.p>
      {onSelectNext && (
        <motion.div className="task-details__empty-actions" variants={reveal.actions}>
          <Button variant="primary" size="sm" onClick={onSelectNext}>
            {t('taskDetails.selectNextTask')}
          </Button>
        </motion.div>
      )}
    </motion.div>
  )
}
